import React from "react";
import { Tooltip } from "@mui/material";
import { SxProps } from "@mui/material";
import { Variant } from "@mui/material/styles/createTypography";
import { PropsWithChildren } from "react";

import { PatientEncounter } from "@/resources/types";
import { Theme } from "@/theme";
import { Text } from "@imminently/react/ui/core/text";

const styles = {
  normalUnderline: {
    borderBottom: "1px #0000004D dashed",
  },
  warningUnderline: {
    borderBottom: "1px #f00 dashed",
  },
};

interface DelegateDataTooltipProps extends PropsWithChildren {
  encounter: PatientEncounter;
  field: string; // Field name to check (e.g., "firstName", "email", "phone")
  value?: string | number | null | undefined; // The current value to display
  variant?: Variant;
  sx?: SxProps<Theme>;
  // Optional: specify where to look for the actual value if not provided
  // "patient" = encounter.patient[field], "encounter" = encounter[field], or a path like "problem.patient"
  valueSource?: "patient" | "encounter" | string;
}

/**
 * Generic component that checks if delegate provided data differs from the actual value.
 * If delegateData[field] exists and differs from the actual value, shows a tooltip.
 * 
 * @example
 * <DelegateDataTooltip encounter={encounter} field="email" value={encounter.patient?.email}>
 *   {encounter.patient?.email || "-"}
 * </DelegateDataTooltip>
 */
const DelegateDataTooltip: React.FC<DelegateDataTooltipProps> = ({
  encounter,
  field,
  value,
  variant,
  sx,
  valueSource = "patient",
  children,
}) => {
  // Get delegate value
  const delegateValue = encounter.delegatedData?.[field as keyof typeof encounter.delegatedData];

  // Get actual value if not provided
  let actualValue: any = value;
  if (actualValue === undefined) {
    if (valueSource === "patient") {
      actualValue = (encounter.patient as any)?.[field];
    } else if (valueSource === "encounter") {
      actualValue = (encounter as any)[field];
    } else {
      // Handle nested paths like "problem.patient"
      const pathParts = valueSource.split(".");
      actualValue = encounter;
      for (const part of pathParts) {
        actualValue = actualValue?.[part];
      }
      // Also check the field at the end of the path
      actualValue = actualValue?.[field];
    }
  }

  // Normalize values for comparison (handle empty strings, null, undefined as equivalent)
  const normalizeForComparison = (val: any): any => {
    if (val === null || val === undefined || val === "") return null;
    if (typeof val === "string") return val.trim();
    return val;
  };

  const normalizedDelegateValue = normalizeForComparison(delegateValue);
  const normalizedActualValue = normalizeForComparison(actualValue);

  // Check if delegate value exists and differs from actual value
  const hasDelegateData = normalizedDelegateValue !== null;
  const valuesDiffer = hasDelegateData && normalizedDelegateValue !== normalizedActualValue;

  // Format delegate value for display
  const formatValue = (val: any): string => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val;
    if (typeof val === "number") return String(val);
    if (typeof val === "boolean") return val ? "Yes" : "No";
    if (Array.isArray(val)) return val.join(", ");
    return String(val);
  };

  // Format delegate value for display (use original value, not normalized)
  const delegateValueFormatted = formatValue(delegateValue);
  const tooltipText = valuesDiffer ? `Delegate provided: ${delegateValueFormatted}` : undefined;

  // If no tooltip needed, just render children or value
  if (!tooltipText) {
    if (children) {
      return <>{children}</>;
    }
    return (
      <Text variant={variant} sx={sx}>
        {formatValue(actualValue) || "-"}
      </Text>
    );
  }

  // Render with tooltip
  const style = {
    ...styles.warningUnderline,
    ...sx,
  };

  return (
    <Tooltip title={tooltipText} arrow>
      <Text variant={variant} sx={style}>
        {children || formatValue(actualValue) || "-"}
      </Text>
    </Tooltip>
  );
};

export default DelegateDataTooltip;

